import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import axios, { CancelToken } from 'axios';
import {
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IColumn,
    IconButton,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    Modal,
    Pivot,
    PivotItem,
    PrimaryButton,
    SearchBox,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { ClusterBanner } from './ClusterBanner';
import { SectionCard } from './SectionCard';
import { KustoTenantDropdown } from './KustoTenantDropdown';
import {
    extractKustoError,
    getKustoAuditTrail,
    grantKustoAppAccess,
    KustoAccessRequestRecord,
    KustoAuditRecord,
    KustoPrincipal,
    KustoTenant,
    listKustoAccessRequests,
    listKustoPrincipals,
    listKustoRequestsForApplication,
    listKustoTenants,
    reviewKustoAccessRequest,
    revokeKustoAppAccess,
} from '../../api/KustoAccessApi';
import { KUSTO_DATABASE as DATABASE } from './kustoAccessConfig';
import {
    getGuidError,
    isGuid,
    isValidDisplayName,
    MAX_DISPLAY_NAME_LENGTH,
    MAX_FREE_TEXT_LENGTH,
} from './kustoAccessValidation';

interface ISortState {
    key: string;
    desc: boolean;
}

/** Case-insensitive, numeric-aware sort over a string projection of a field. */
const copyAndSort = <T,>(
    items: T[],
    columnKey: string,
    descending: boolean
): T[] => {
    const key = columnKey as keyof T;
    return items.slice(0).sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        const as = av === undefined || av === null ? '' : String(av);
        const bs = bv === undefined || bv === null ? '' : String(bv);
        const cmp = as.localeCompare(bs, undefined, {
            numeric: true,
            sensitivity: 'base',
        });
        return descending ? -cmp : cmp;
    });
};

/** Applies the current sort state (arrow + click handler) to a set of columns. */
const applySort = (
    columns: IColumn[],
    sort: ISortState,
    onColumnClick: (
        ev?: React.MouseEvent<HTMLElement>,
        column?: IColumn
    ) => void
): IColumn[] =>
    columns.map((column) =>
        column.fieldName
            ? {
                  ...column,
                  isSorted: sort.key === column.fieldName,
                  isSortedDescending:
                      sort.key === column.fieldName && sort.desc,
                  onColumnClick,
              }
            : column
    );

/**
 * Renders a cell whose value is often too long for the column: the grid still truncates it,
 * but the full value is available on hover (and in the row's details modal).
 */
const renderTruncatedCell = (value?: string | null): JSX.Element => (
    <span title={value ?? undefined}>{value ?? '\u2014'}</span>
);

/** A single label / value pair inside a details modal. Wraps instead of truncating. */
const DetailField: React.FC<{ label: string; value?: string | null }> = ({
    label,
    value,
}) => (
    <Stack tokens={{ childrenGap: 2 }}>
        <Text variant="smallPlus" styles={{ root: { fontWeight: 600 } }}>
            {label}
        </Text>
        <Text
            variant="medium"
            styles={{
                root: { wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
            }}
        >
            {value === undefined || value === null || value === ''
                ? '\u2014'
                : value}
        </Text>
    </Stack>
);

/**
 * Admin page for auditing and managing Kusto access to the SovereignReleaseStatus database:
 * the live principal list (with revoke) and the grant/revoke audit trail.
 */
export const KustoAdminPage: React.FC = () => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const [principals, setPrincipals] = useState<KustoPrincipal[]>([]);
    const [audit, setAudit] = useState<KustoAuditRecord[]>([]);
    const [loadingPrincipals, setLoadingPrincipals] = useState<boolean>(false);
    const [loadingAudit, setLoadingAudit] = useState<boolean>(false);
    const [loadingRequests, setLoadingRequests] = useState<boolean>(false);
    const [loadingTenants, setLoadingTenants] = useState<boolean>(true);
    const [tenants, setTenants] = useState<KustoTenant[]>([]);
    const loading =
        loadingPrincipals || loadingAudit || loadingRequests || loadingTenants;
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [revokeTarget, setRevokeTarget] = useState<KustoPrincipal | null>(
        null
    );
    const [revoking, setRevoking] = useState<boolean>(false);

    // Row whose full details are shown in a modal (long values are truncated in the grid).
    const [principalDetails, setPrincipalDetails] =
        useState<KustoPrincipal | null>(null);
    const [auditDetails, setAuditDetails] = useState<KustoAuditRecord | null>(
        null
    );
    const [requestDetails, setRequestDetails] =
        useState<KustoAccessRequestRecord | null>(null);

    // Principal search / filter / sort.
    const [principalSearch, setPrincipalSearch] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [principalSort, setPrincipalSort] = useState<ISortState>({
        key: 'role',
        desc: false,
    });

    // Audit search / filter / sort.
    const [auditSearch, setAuditSearch] = useState<string>('');
    const [actionFilter, setActionFilter] = useState<string>('all');
    const [resultFilter, setResultFilter] = useState<string>('all');
    const [auditSort, setAuditSort] = useState<ISortState>({
        key: 'timestampUtc',
        desc: true,
    });

    // Review queue.
    const [requests, setRequests] = useState<KustoAccessRequestRecord[]>([]);
    const [requestStatusFilter, setRequestStatusFilter] =
        useState<string>('Pending');
    const [reviewTarget, setReviewTarget] =
        useState<KustoAccessRequestRecord | null>(null);
    const [reviewApprove, setReviewApprove] = useState<boolean>(true);
    const [reviewNotes, setReviewNotes] = useState<string>('');
    const [reviewError, setReviewError] = useState<string | null>(null);
    const [reviewing, setReviewing] = useState<boolean>(false);
    const [appHistory, setAppHistory] = useState<KustoAccessRequestRecord[]>(
        []
    );

    // Direct grant (admin shortcut that bypasses the queue).
    const [addAppId, setAddAppId] = useState<string>('');
    const [addTenantId, setAddTenantId] = useState<string>('');
    const [addName, setAddName] = useState<string>('');
    const [addJustification, setAddJustification] = useState<string>('');
    const [adding, setAdding] = useState<boolean>(false);

    const load = useCallback(async (cancelToken?: CancelToken) => {
        setError(null);
        // Each section renders as soon as its own call returns, so the Cosmos-backed review queue
        // is usable without waiting on the slower Kusto principal list.
        const failures: string[] = [];

        const track = async (
            setBusy: (busy: boolean) => void,
            run: () => Promise<void>
        ): Promise<void> => {
            setBusy(true);
            try {
                await run();
            } catch (err) {
                if (!axios.isCancel(err)) {
                    failures.push(extractKustoError(err));
                }
            } finally {
                setBusy(false);
            }
        };

        await Promise.all([
            track(setLoadingTenants, async () => {
                setTenants([]);
                const result = await listKustoTenants(cancelToken);
                setTenants(result.data ?? []);
            }),
            track(setLoadingRequests, async () => {
                const result = await listKustoAccessRequests(
                    undefined,
                    cancelToken
                );
                setRequests(result.data ?? []);
            }),
            track(setLoadingPrincipals, async () => {
                const result = await listKustoPrincipals(cancelToken);
                setPrincipals(result.data ?? []);
            }),
            track(setLoadingAudit, async () => {
                const result = await getKustoAuditTrail(100, cancelToken);
                setAudit(result.data ?? []);
            }),
        ]);

        setError(
            failures.length > 0 ? Array.from(new Set(failures)).join(' ') : null
        );
    }, []);

    useEffect(() => {
        // Cancel the in-flight load if the component unmounts so we don't set state
        // on an unmounted component.
        const source = axios.CancelToken.source();
        void load(source.token);
        return () => source.cancel();
    }, [load]);

    const confirmRevoke = useCallback(async () => {
        if (!revokeTarget?.applicationId || !revokeTarget?.tenantId) {
            setRevokeTarget(null);
            return;
        }
        setRevoking(true);
        setError(null);
        setMessage(null);
        try {
            const result = await revokeKustoAppAccess({
                applicationId: revokeTarget.applicationId,
                tenantId: revokeTarget.tenantId,
            });
            setMessage(result.message ?? 'Access revoked.');
            setRevokeTarget(null);
            await load();
        } catch (err) {
            setError(extractKustoError(err));
        } finally {
            setRevoking(false);
        }
    }, [revokeTarget, load]);

    // Pull the full history for the target application so the reviewer can see whether anyone
    // else still depends on this access before approving a removal.
    const openReview = useCallback(
        async (item: KustoAccessRequestRecord, approve: boolean) => {
            setReviewTarget(item);
            setReviewApprove(approve);
            setReviewNotes('');
            setReviewError(null);
            setAppHistory([]);
            if (item.applicationId) {
                try {
                    const result = await listKustoRequestsForApplication(
                        item.applicationId
                    );
                    setAppHistory(result.data ?? []);
                } catch {
                    setAppHistory([]);
                }
            }
        },
        []
    );

    const confirmReview = useCallback(async () => {
        if (!reviewTarget?.id) {
            setReviewTarget(null);
            return;
        }
        setReviewing(true);
        setError(null);
        setReviewError(null);
        setMessage(null);
        try {
            const result = await reviewKustoAccessRequest(
                reviewTarget.id,
                reviewApprove,
                reviewNotes.trim() || undefined
            );
            setMessage(
                result.message ??
                    (reviewApprove ? 'Request approved.' : 'Request denied.')
            );
            setReviewTarget(null);
            await load();
        } catch (err) {
            setReviewError(extractKustoError(err));
        } finally {
            setReviewing(false);
        }
    }, [reviewTarget, reviewApprove, reviewNotes, load]);

    const confirmAdd = useCallback(async () => {
        setAdding(true);
        setError(null);
        setMessage(null);
        try {
            const result = await grantKustoAppAccess({
                applicationId: addAppId.trim(),
                tenantId: addTenantId.trim(),
                displayName: addName.trim(),
                justification: addJustification.trim() || undefined,
            });
            setMessage(result.message ?? 'Access granted.');
            setAddAppId('');
            setAddTenantId('');
            setAddName('');
            setAddJustification('');
            await load();
        } catch (err) {
            setError(extractKustoError(err));
        } finally {
            setAdding(false);
        }
    }, [addAppId, addTenantId, addName, addJustification, load]);

    const onPrincipalColumnClick = useCallback(
        (_ev?: React.MouseEvent<HTMLElement>, column?: IColumn): void => {
            if (!column?.fieldName) {
                return;
            }
            const field = column.fieldName;
            setPrincipalSort((prev) =>
                prev.key === field
                    ? { key: field, desc: !prev.desc }
                    : { key: field, desc: false }
            );
        },
        []
    );

    const onAuditColumnClick = useCallback(
        (_ev?: React.MouseEvent<HTMLElement>, column?: IColumn): void => {
            if (!column?.fieldName) {
                return;
            }
            const field = column.fieldName;
            setAuditSort((prev) =>
                prev.key === field
                    ? { key: field, desc: !prev.desc }
                    : { key: field, desc: false }
            );
        },
        []
    );

    const typeOptions: IDropdownOption[] = useMemo(() => {
        const distinct = Array.from(
            new Set(
                principals
                    .map((p) => p.principalType)
                    .filter((t): t is string => !!t)
            )
        ).sort((a, b) => a.localeCompare(b));
        return [
            { key: 'all', text: 'All types' },
            ...distinct.map((t) => ({ key: t, text: t })),
        ];
    }, [principals]);

    const filteredPrincipals = useMemo(() => {
        const query = principalSearch.trim().toLowerCase();
        const filtered = principals.filter((p) => {
            if (
                typeFilter !== 'all' &&
                (p.principalType ?? '') !== typeFilter
            ) {
                return false;
            }
            if (!query) {
                return true;
            }
            return [
                p.role,
                p.principalType,
                p.displayName,
                p.applicationId,
                p.tenantId,
                p.objectId,
                p.fqn,
            ].some((v) => (v ?? '').toLowerCase().includes(query));
        });
        return copyAndSort(filtered, principalSort.key, principalSort.desc);
    }, [principals, principalSearch, typeFilter, principalSort]);

    const filteredAudit = useMemo(() => {
        const query = auditSearch.trim().toLowerCase();
        const filtered = audit.filter((a) => {
            if (
                actionFilter !== 'all' &&
                (a.action ?? '').toLowerCase() !== actionFilter
            ) {
                return false;
            }
            if (resultFilter === 'ok' && !a.succeeded) {
                return false;
            }
            if (resultFilter === 'failed' && a.succeeded) {
                return false;
            }
            if (!query) {
                return true;
            }
            return [
                a.action,
                a.applicationId,
                a.tenantId,
                a.displayName,
                a.requestedBy,
                a.justification,
                a.reviewNotes,
                a.message,
            ].some((v) => (v ?? '').toLowerCase().includes(query));
        });
        return copyAndSort(filtered, auditSort.key, auditSort.desc);
    }, [audit, auditSearch, actionFilter, resultFilter, auditSort]);

    const filteredRequests = useMemo(
        () =>
            requests.filter(
                (r) =>
                    requestStatusFilter === 'all' ||
                    (r.status ?? '') === requestStatusFilter
            ),
        [requests, requestStatusFilter]
    );

    const pendingCount = useMemo(
        () => requests.filter((r) => r.status === 'Pending').length,
        [requests]
    );

    const requestColumns: IColumn[] = [
        {
            key: 'requestedOn',
            name: 'Requested',
            fieldName: 'requestedOn',
            minWidth: 140,
            maxWidth: 170,
            isResizable: true,
            onRender: (item?: KustoAccessRequestRecord) => (
                <span>
                    {item ? new Date(item.requestedOn).toLocaleString() : ''}
                </span>
            ),
        },
        {
            key: 'requestAction',
            name: 'Action',
            fieldName: 'action',
            minWidth: 70,
            maxWidth: 90,
        },
        {
            key: 'requestApp',
            name: 'Application',
            fieldName: 'displayName',
            minWidth: 160,
            isResizable: true,
            isMultiline: true,
            onRender: (item?: KustoAccessRequestRecord) => (
                <Stack tokens={{ childrenGap: 4 }}>
                    {renderTruncatedCell(
                        item?.displayName ?? item?.applicationId
                    )}
                    {item?.failureMessage && (
                        <Text
                            block
                            styles={{
                                root: {
                                    color: theme.semanticColors.errorText,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                },
                            }}
                        >
                            {item.failureMessage}
                        </Text>
                    )}
                </Stack>
            ),
        },
        {
            key: 'requestBy',
            name: 'Requested by',
            fieldName: 'requestedBy',
            minWidth: 160,
            isResizable: true,
            onRender: (item?: KustoAccessRequestRecord) =>
                renderTruncatedCell(item?.requestedBy),
        },
        {
            key: 'requestJust',
            name: 'Justification',
            fieldName: 'justification',
            minWidth: 160,
            isResizable: true,
            onRender: (item?: KustoAccessRequestRecord) =>
                renderTruncatedCell(item?.justification),
        },
        {
            key: 'requestStatus',
            name: 'Status',
            fieldName: 'status',
            minWidth: 90,
            maxWidth: 110,
        },
        {
            key: 'requestDetails',
            name: '',
            minWidth: 36,
            maxWidth: 36,
            onRender: (item?: KustoAccessRequestRecord) =>
                item ? (
                    <IconButton
                        iconProps={{ iconName: 'Info' }}
                        title="Request details"
                        ariaLabel={`View request details for ${
                            item.displayName ?? item.applicationId
                        }`}
                        onClick={() => setRequestDetails(item)}
                    />
                ) : null,
        },
        {
            key: 'requestActions',
            name: '',
            // Wide enough for Approve + Deny side by side, and capped so the justified layout
            // gives the surplus to the text columns rather than to the buttons.
            minWidth: 210,
            maxWidth: 210,
            onRender: (item?: KustoAccessRequestRecord) =>
                item?.status === 'Pending' ? (
                    <Stack horizontal tokens={{ childrenGap: 4 }}>
                        <PrimaryButton
                            text="Approve"
                            iconProps={{ iconName: 'CheckMark' }}
                            onClick={() => void openReview(item, true)}
                        />
                        <DefaultButton
                            text="Deny"
                            iconProps={{ iconName: 'Cancel' }}
                            onClick={() => void openReview(item, false)}
                        />
                    </Stack>
                ) : null,
        },
    ];

    const principalColumns: IColumn[] = [
        {
            key: 'role',
            name: 'Role',
            fieldName: 'role',
            minWidth: 200,
            maxWidth: 280,
            isResizable: true,
        },
        {
            key: 'type',
            name: 'Type',
            fieldName: 'principalType',
            minWidth: 110,
            maxWidth: 150,
            isResizable: true,
        },
        {
            key: 'name',
            name: 'Display name',
            fieldName: 'displayName',
            minWidth: 160,
            isResizable: true,
            onRender: (item?: KustoPrincipal) =>
                renderTruncatedCell(item?.displayName),
        },
        {
            key: 'appId',
            name: 'Application ID',
            fieldName: 'applicationId',
            minWidth: 260,
            isResizable: true,
            onRender: (item?: KustoPrincipal) =>
                renderTruncatedCell(item?.applicationId),
        },
        {
            key: 'actions',
            name: '',
            minWidth: 150,
            maxWidth: 150,
            onRender: (item?: KustoPrincipal) =>
                item ? (
                    <Stack
                        horizontal
                        tokens={{ childrenGap: 4 }}
                        verticalAlign="center"
                    >
                        {item.applicationId ? (
                            <DefaultButton
                                text="Revoke"
                                iconProps={{ iconName: 'BlockedSite' }}
                                onClick={() => setRevokeTarget(item)}
                            />
                        ) : null}
                        <IconButton
                            iconProps={{ iconName: 'Info' }}
                            title="View details"
                            ariaLabel={`View details for ${
                                item.displayName ??
                                item.applicationId ??
                                'principal'
                            }`}
                            onClick={() => setPrincipalDetails(item)}
                        />
                    </Stack>
                ) : null,
        },
    ];

    const auditColumns: IColumn[] = [
        {
            key: 'time',
            name: 'Time (UTC)',
            fieldName: 'timestampUtc',
            minWidth: 160,
            maxWidth: 190,
            isResizable: true,
            onRender: (item?: KustoAuditRecord) => (
                <span>
                    {item ? new Date(item.timestampUtc).toLocaleString() : ''}
                </span>
            ),
        },
        {
            key: 'action',
            name: 'Action',
            fieldName: 'action',
            minWidth: 70,
            maxWidth: 90,
        },
        {
            key: 'appId',
            name: 'Application ID',
            fieldName: 'applicationId',
            minWidth: 240,
            isResizable: true,
            onRender: (item?: KustoAuditRecord) =>
                renderTruncatedCell(item?.applicationId),
        },
        {
            key: 'by',
            name: 'Requested by',
            fieldName: 'requestedBy',
            minWidth: 160,
            isResizable: true,
            onRender: (item?: KustoAuditRecord) =>
                renderTruncatedCell(item?.requestedBy),
        },
        {
            key: 'just',
            name: 'Justification',
            fieldName: 'justification',
            minWidth: 180,
            isResizable: true,
            onRender: (item?: KustoAuditRecord) =>
                renderTruncatedCell(item?.justification),
        },
        {
            key: 'result',
            name: 'Result',
            fieldName: 'succeeded',
            minWidth: 70,
            onRender: (item?: KustoAuditRecord) =>
                item?.succeeded ? (
                    <span>OK</span>
                ) : (
                    <span
                        title={item?.message ?? undefined}
                        style={{ color: theme.semanticColors.errorText }}
                    >
                        Failed
                    </span>
                ),
        },
        {
            key: 'auditDetails',
            name: '',
            minWidth: 40,
            maxWidth: 40,
            onRender: (item?: KustoAuditRecord) =>
                item ? (
                    <IconButton
                        iconProps={{ iconName: 'Info' }}
                        title="View details"
                        ariaLabel={`View details for the ${
                            item.action ?? 'audit'
                        } of ${item.applicationId ?? 'application'}`}
                        onClick={() => setAuditDetails(item)}
                    />
                ) : null,
        },
    ];

    const requestStatusOptions: IDropdownOption[] = [
        { key: 'Pending', text: 'Pending' },
        { key: 'Approved', text: 'Approved' },
        { key: 'Denied', text: 'Denied' },
        { key: 'Cancelled', text: 'Cancelled' },
        { key: 'all', text: 'All requests' },
    ];

    const actionOptions: IDropdownOption[] = [
        { key: 'all', text: 'All actions' },
        { key: 'grant', text: 'Grant' },
        { key: 'revoke', text: 'Revoke' },
        { key: 'deny', text: 'Deny' },
    ];

    const resultOptions: IDropdownOption[] = [
        { key: 'all', text: 'All results' },
        { key: 'ok', text: 'Succeeded' },
        { key: 'failed', text: 'Failed' },
    ];

    // Keep the search box and filter dropdowns the same height so the toolbar
    // controls line up on one baseline.
    const searchStyles = { root: { width: 300, height: 32 } };
    const makeDropdownStyles = (width: number) => ({
        root: { width },
        dropdown: { height: 32 },
        title: { height: 32, lineHeight: '30px' },
        caretDownWrapper: { height: 32, lineHeight: '30px' },
    });

    return (
        <>
            <div
                style={{
                    width: '100%',
                    maxWidth: '100%',
                    margin: '0 auto',
                    padding: 24,
                    boxSizing: 'border-box',
                }}
            >
                <Stack tokens={{ childrenGap: 20 }}>
                    <Stack
                        horizontal
                        horizontalAlign="space-between"
                        verticalAlign="start"
                        tokens={{ childrenGap: 16 }}
                    >
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text
                                variant="xxLarge"
                                styles={{ root: { fontWeight: 700 } }}
                            >
                                Kusto admin
                            </Text>
                            <Text
                                variant="medium"
                                styles={{
                                    root: {
                                        color: theme.palette.neutralSecondary,
                                    },
                                }}
                            >
                                {`Audit and manage access to the ${DATABASE} database`}
                            </Text>
                        </Stack>
                        <DefaultButton
                            text="Refresh"
                            iconProps={{ iconName: 'Refresh' }}
                            onClick={() => void load()}
                            disabled={loading}
                        />
                    </Stack>

                    <ClusterBanner label="Cluster endpoint you are managing" />

                    {error && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {error}
                        </MessageBar>
                    )}
                    {message && (
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            onDismiss={() => setMessage(null)}
                        >
                            {message}
                        </MessageBar>
                    )}

                    <SectionCard
                        icon="Shield"
                        title="Access management"
                        description="Review who can query the database and the history of grants and revokes."
                    >
                        <Pivot
                            styles={{
                                root: { maxWidth: '100%', overflowX: 'auto' },
                            }}
                        >
                            <PivotItem
                                headerText="Requests"
                                itemIcon="Inbox"
                                itemCount={pendingCount}
                            >
                                <Stack
                                    tokens={{ childrenGap: 14 }}
                                    styles={{ root: { paddingTop: 16 } }}
                                >
                                    <Stack
                                        horizontal
                                        wrap
                                        tokens={{ childrenGap: 12 }}
                                        verticalAlign="center"
                                    >
                                        <Dropdown
                                            ariaLabel="Filter requests by status"
                                            options={requestStatusOptions}
                                            selectedKey={requestStatusFilter}
                                            onChange={(_, o) =>
                                                setRequestStatusFilter(
                                                    (o?.key as string) ??
                                                        'Pending'
                                                )
                                            }
                                            styles={makeDropdownStyles(180)}
                                        />
                                    </Stack>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                            },
                                        }}
                                    >
                                        {`Showing ${filteredRequests.length} of ${requests.length}`}
                                    </Text>
                                    {loadingRequests && (
                                        <Spinner
                                            size={SpinnerSize.medium}
                                            label="Loading requests…"
                                            labelPosition="right"
                                        />
                                    )}
                                    {!loadingRequests &&
                                    filteredRequests.length === 0 ? (
                                        <Text
                                            variant="medium"
                                            styles={{
                                                root: {
                                                    color: theme.palette
                                                        .neutralSecondary,
                                                },
                                            }}
                                        >
                                            No requests match this filter.
                                        </Text>
                                    ) : (
                                        <DetailsList
                                            items={filteredRequests}
                                            columns={requestColumns}
                                            selectionMode={SelectionMode.none}
                                            layoutMode={
                                                DetailsListLayoutMode.justified
                                            }
                                        />
                                    )}
                                </Stack>
                            </PivotItem>
                            <PivotItem
                                headerText="Grant access"
                                itemIcon="Permissions"
                            >
                                <Stack
                                    tokens={{ childrenGap: 14 }}
                                    styles={{ root: { paddingTop: 16 } }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                            },
                                        }}
                                    >
                                        Grants viewer access immediately,
                                        without going through the request queue.
                                        Recorded in the audit trail.
                                    </Text>
                                    <Stack
                                        tokens={{ childrenGap: 12 }}
                                        styles={{ root: { maxWidth: 440 } }}
                                    >
                                        <TextField
                                            label="Application (client) ID"
                                            placeholder="00000000-0000-0000-0000-000000000000"
                                            value={addAppId}
                                            required
                                            errorMessage={getGuidError(
                                                addAppId,
                                                'Application (client) ID'
                                            )}
                                            onChange={(_, v) =>
                                                setAddAppId(v ?? '')
                                            }
                                        />
                                        <KustoTenantDropdown
                                            tenants={tenants}
                                            selectedTenantId={addTenantId}
                                            required
                                            disabled={
                                                loadingTenants ||
                                                tenants.length === 0
                                            }
                                            errorMessage={
                                                !loadingTenants &&
                                                tenants.length === 0
                                                    ? 'No supported tenants are available.'
                                                    : undefined
                                            }
                                            onChange={setAddTenantId}
                                        />
                                        <TextField
                                            label="Application / service name"
                                            value={addName}
                                            required
                                            maxLength={MAX_DISPLAY_NAME_LENGTH}
                                            errorMessage={
                                                addName.trim() &&
                                                !isValidDisplayName(addName)
                                                    ? 'Use letters, digits, spaces and . _ - ( ) / , only.'
                                                    : undefined
                                            }
                                            onChange={(_, v) =>
                                                setAddName(v ?? '')
                                            }
                                        />
                                        <TextField
                                            label="Business justification"
                                            multiline
                                            rows={3}
                                            value={addJustification}
                                            maxLength={MAX_FREE_TEXT_LENGTH}
                                            onChange={(_, v) =>
                                                setAddJustification(v ?? '')
                                            }
                                        />
                                    </Stack>
                                    <Stack
                                        horizontal
                                        tokens={{ childrenGap: 12 }}
                                        verticalAlign="center"
                                    >
                                        <PrimaryButton
                                            text="Grant access"
                                            iconProps={{
                                                iconName: 'Permissions',
                                            }}
                                            onClick={() => void confirmAdd()}
                                            disabled={
                                                adding ||
                                                !isGuid(addAppId) ||
                                                loadingTenants ||
                                                !tenants.some(
                                                    (tenant) =>
                                                        tenant.tenantId ===
                                                        addTenantId
                                                ) ||
                                                !isValidDisplayName(addName) ||
                                                addJustification.trim().length >
                                                    MAX_FREE_TEXT_LENGTH
                                            }
                                        />
                                        {adding && (
                                            <Spinner
                                                size={SpinnerSize.small}
                                                label="Granting…"
                                                labelPosition="right"
                                            />
                                        )}
                                    </Stack>
                                </Stack>
                            </PivotItem>
                            <PivotItem
                                headerText="Supported tenants"
                                itemIcon="Org"
                            >
                                <Stack
                                    tokens={{ childrenGap: 16 }}
                                    styles={{ root: { paddingTop: 16 } }}
                                >
                                    {loadingTenants && (
                                        <Spinner label="Loading supported tenants..." />
                                    )}
                                    {!loadingTenants &&
                                        tenants.length === 0 && (
                                            <MessageBar
                                                messageBarType={
                                                    MessageBarType.warning
                                                }
                                            >
                                                No supported tenants are
                                                configured.
                                            </MessageBar>
                                        )}
                                    {tenants.length > 0 && (
                                        <DetailsList
                                            items={tenants}
                                            columns={[
                                                {
                                                    key: 'displayName',
                                                    fieldName: 'displayName',
                                                    name: 'Tenant name',
                                                    minWidth: 160,
                                                    isMultiline: true,
                                                },
                                                {
                                                    key: 'tenantId',
                                                    fieldName: 'tenantId',
                                                    name: 'Directory (tenant) ID',
                                                    minWidth: 280,
                                                    isResizable: true,
                                                },
                                            ]}
                                            selectionMode={SelectionMode.none}
                                            layoutMode={
                                                DetailsListLayoutMode.justified
                                            }
                                        />
                                    )}
                                </Stack>
                            </PivotItem>
                            <PivotItem
                                headerText="Current principals"
                                itemIcon="SecurityGroup"
                                itemCount={principals.length}
                            >
                                <Stack
                                    tokens={{ childrenGap: 14 }}
                                    styles={{ root: { paddingTop: 16 } }}
                                >
                                    <Stack
                                        horizontal
                                        wrap
                                        tokens={{ childrenGap: 12 }}
                                        verticalAlign="center"
                                    >
                                        <SearchBox
                                            placeholder="Search role, name, app ID…"
                                            value={principalSearch}
                                            onChange={(_, v) =>
                                                setPrincipalSearch(v ?? '')
                                            }
                                            onClear={() =>
                                                setPrincipalSearch('')
                                            }
                                            styles={searchStyles}
                                        />
                                        <Dropdown
                                            ariaLabel="Filter by principal type"
                                            options={typeOptions}
                                            selectedKey={typeFilter}
                                            onChange={(_, o) =>
                                                setTypeFilter(
                                                    (o?.key as string) ?? 'all'
                                                )
                                            }
                                            styles={makeDropdownStyles(200)}
                                        />
                                    </Stack>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                            },
                                        }}
                                    >
                                        {`Showing ${filteredPrincipals.length} of ${principals.length}`}
                                    </Text>
                                    {loadingPrincipals && (
                                        <Spinner
                                            size={SpinnerSize.medium}
                                            label="Loading principals from Kusto…"
                                            labelPosition="right"
                                        />
                                    )}
                                    {!loadingPrincipals &&
                                    filteredPrincipals.length === 0 ? (
                                        <Text
                                            variant="medium"
                                            styles={{
                                                root: {
                                                    color: theme.palette
                                                        .neutralSecondary,
                                                },
                                            }}
                                        >
                                            No principals match your search.
                                        </Text>
                                    ) : (
                                        <DetailsList
                                            items={filteredPrincipals}
                                            columns={applySort(
                                                principalColumns,
                                                principalSort,
                                                onPrincipalColumnClick
                                            )}
                                            selectionMode={SelectionMode.none}
                                            layoutMode={
                                                DetailsListLayoutMode.justified
                                            }
                                        />
                                    )}
                                </Stack>
                            </PivotItem>
                            <PivotItem
                                headerText="Audit trail"
                                itemIcon="History"
                                itemCount={audit.length}
                            >
                                <Stack
                                    tokens={{ childrenGap: 14 }}
                                    styles={{ root: { paddingTop: 16 } }}
                                >
                                    <Stack
                                        horizontal
                                        wrap
                                        tokens={{ childrenGap: 12 }}
                                        verticalAlign="center"
                                    >
                                        <SearchBox
                                            placeholder="Search app ID, requester, justification…"
                                            value={auditSearch}
                                            onChange={(_, v) =>
                                                setAuditSearch(v ?? '')
                                            }
                                            onClear={() => setAuditSearch('')}
                                            styles={searchStyles}
                                        />
                                        <Dropdown
                                            ariaLabel="Filter by action"
                                            options={actionOptions}
                                            selectedKey={actionFilter}
                                            onChange={(_, o) =>
                                                setActionFilter(
                                                    (o?.key as string) ?? 'all'
                                                )
                                            }
                                            styles={makeDropdownStyles(160)}
                                        />
                                        <Dropdown
                                            ariaLabel="Filter by result"
                                            options={resultOptions}
                                            selectedKey={resultFilter}
                                            onChange={(_, o) =>
                                                setResultFilter(
                                                    (o?.key as string) ?? 'all'
                                                )
                                            }
                                            styles={makeDropdownStyles(160)}
                                        />
                                    </Stack>
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .neutralSecondary,
                                            },
                                        }}
                                    >
                                        {`Showing ${filteredAudit.length} of ${audit.length}`}
                                    </Text>
                                    {loadingAudit && (
                                        <Spinner
                                            size={SpinnerSize.medium}
                                            label="Loading audit trail…"
                                            labelPosition="right"
                                        />
                                    )}
                                    {!loadingAudit &&
                                    filteredAudit.length === 0 ? (
                                        <Text
                                            variant="medium"
                                            styles={{
                                                root: {
                                                    color: theme.palette
                                                        .neutralSecondary,
                                                },
                                            }}
                                        >
                                            No audit records match your search.
                                        </Text>
                                    ) : (
                                        <DetailsList
                                            items={filteredAudit}
                                            columns={applySort(
                                                auditColumns,
                                                auditSort,
                                                onAuditColumnClick
                                            )}
                                            selectionMode={SelectionMode.none}
                                            layoutMode={
                                                DetailsListLayoutMode.justified
                                            }
                                        />
                                    )}
                                </Stack>
                            </PivotItem>
                        </Pivot>
                    </SectionCard>
                </Stack>
            </div>

            <Dialog
                hidden={!revokeTarget}
                onDismiss={() => setRevokeTarget(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Revoke access?',
                    subText: revokeTarget
                        ? `Remove viewer access for "${
                              revokeTarget.displayName ??
                              revokeTarget.applicationId
                          }" (${
                              revokeTarget.applicationId
                          })? This is recorded in the audit trail.`
                        : '',
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text="Revoke"
                        onClick={() => void confirmRevoke()}
                        disabled={revoking}
                    />
                    <DefaultButton
                        text="Cancel"
                        onClick={() => setRevokeTarget(null)}
                        disabled={revoking}
                    />
                </DialogFooter>
            </Dialog>

            <Dialog
                hidden={!reviewTarget}
                onDismiss={() => setReviewTarget(null)}
                minWidth={520}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: reviewApprove ? 'Approve request?' : 'Deny request?',
                    subText: reviewTarget
                        ? `${reviewTarget.action} access for "${
                              reviewTarget.displayName ??
                              reviewTarget.applicationId
                          }" requested by ${reviewTarget.requestedBy}.`
                        : '',
                }}
            >
                <Stack tokens={{ childrenGap: 12 }}>
                    {reviewError && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {reviewError}
                        </MessageBar>
                    )}
                    <DetailField
                        label="Application (client) ID"
                        value={reviewTarget?.applicationId}
                    />
                    <DetailField
                        label="Directory (tenant) ID"
                        value={reviewTarget?.tenantId}
                    />
                    {reviewTarget?.justification && (
                        <Text variant="small">
                            <strong>Justification: </strong>
                            {reviewTarget.justification}
                        </Text>
                    )}
                    {reviewApprove &&
                        reviewTarget?.action === 'Revoke' &&
                        appHistory.filter(
                            (h) =>
                                h.status === 'Approved' &&
                                h.action === 'Grant' &&
                                h.requestedBy !== reviewTarget.requestedBy
                        ).length > 0 && (
                            <MessageBar messageBarType={MessageBarType.warning}>
                                {`Other people also hold approved access to this application (${appHistory
                                    .filter(
                                        (h) =>
                                            h.status === 'Approved' &&
                                            h.action === 'Grant' &&
                                            h.requestedBy !==
                                                reviewTarget.requestedBy
                                    )
                                    .map((h) => h.requestedBy)
                                    .join(
                                        ', '
                                    )}). Removing it will affect them too.`}
                            </MessageBar>
                        )}
                    <TextField
                        label="Review note (not shown to the requester)"
                        multiline
                        rows={3}
                        value={reviewNotes}
                        maxLength={MAX_FREE_TEXT_LENGTH}
                        onChange={(_, v) => setReviewNotes(v ?? '')}
                    />
                </Stack>
                <DialogFooter>
                    <PrimaryButton
                        text={reviewApprove ? 'Approve' : 'Deny'}
                        onClick={() => void confirmReview()}
                        disabled={reviewing}
                    />
                    <DefaultButton
                        text="Cancel"
                        onClick={() => setReviewTarget(null)}
                        disabled={reviewing}
                    />
                </DialogFooter>
            </Dialog>

            <Modal
                isOpen={!!requestDetails}
                onDismiss={() => setRequestDetails(null)}
                titleAriaId="kusto-request-details-title"
            >
                <Stack
                    tokens={{ childrenGap: 14 }}
                    styles={{
                        root: {
                            padding: 24,
                            width: 560,
                            maxWidth: 'calc(100vw - 32px)',
                            boxSizing: 'border-box',
                        },
                    }}
                >
                    <Stack
                        horizontal
                        horizontalAlign="space-between"
                        verticalAlign="center"
                    >
                        <Text
                            id="kusto-request-details-title"
                            variant="xLarge"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            Request details
                        </Text>
                        <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            title="Close request details"
                            ariaLabel="Close request details"
                            onClick={() => setRequestDetails(null)}
                        />
                    </Stack>
                    <DetailField
                        label="Request ID"
                        value={requestDetails?.id}
                    />
                    <DetailField
                        label="Action"
                        value={requestDetails?.action}
                    />
                    <DetailField
                        label="Status"
                        value={requestDetails?.status}
                    />
                    <DetailField
                        label="Application / service name"
                        value={requestDetails?.displayName}
                    />
                    <DetailField
                        label="Application (client) ID"
                        value={requestDetails?.applicationId}
                    />
                    <DetailField
                        label="Directory (tenant) ID"
                        value={requestDetails?.tenantId}
                    />
                    <DetailField
                        label="Requested by"
                        value={requestDetails?.requestedBy}
                    />
                    <DetailField
                        label="Requested on"
                        value={
                            requestDetails?.requestedOn
                                ? new Date(
                                      requestDetails.requestedOn
                                  ).toLocaleString()
                                : undefined
                        }
                    />
                    <DetailField
                        label="Business justification"
                        value={requestDetails?.justification}
                    />
                    {requestDetails?.failureMessage && (
                        <DetailField
                            label="Last approval error"
                            value={requestDetails.failureMessage}
                        />
                    )}
                    {requestDetails?.reviewedBy && (
                        <DetailField
                            label="Reviewed by"
                            value={requestDetails.reviewedBy}
                        />
                    )}
                    {requestDetails?.reviewedOn && (
                        <DetailField
                            label="Reviewed on"
                            value={new Date(
                                requestDetails.reviewedOn
                            ).toLocaleString()}
                        />
                    )}
                    {requestDetails?.reviewNotes && (
                        <DetailField
                            label="Reviewer note"
                            value={requestDetails.reviewNotes}
                        />
                    )}
                </Stack>
            </Modal>

            <Modal
                isOpen={!!principalDetails}
                onDismiss={() => setPrincipalDetails(null)}
                titleAriaId="kusto-principal-details-title"
            >
                <Stack
                    tokens={{ childrenGap: 14 }}
                    styles={{
                        root: { padding: 24, width: 560, maxWidth: '90vw' },
                    }}
                >
                    <Stack
                        horizontal
                        horizontalAlign="space-between"
                        verticalAlign="center"
                    >
                        <Text
                            id="kusto-principal-details-title"
                            variant="xLarge"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            Principal details
                        </Text>
                        <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            ariaLabel="Close"
                            onClick={() => setPrincipalDetails(null)}
                        />
                    </Stack>
                    <DetailField label="Role" value={principalDetails?.role} />
                    <DetailField
                        label="Type"
                        value={principalDetails?.principalType}
                    />
                    <DetailField
                        label="Display name"
                        value={principalDetails?.displayName}
                    />
                    <DetailField
                        label="Application ID"
                        value={principalDetails?.applicationId}
                    />
                    <DetailField
                        label="Tenant ID"
                        value={principalDetails?.tenantId}
                    />
                    <DetailField
                        label="Object ID"
                        value={principalDetails?.objectId}
                    />
                    <DetailField
                        label="Principal FQN"
                        value={principalDetails?.fqn}
                    />
                </Stack>
            </Modal>

            <Modal
                isOpen={!!auditDetails}
                onDismiss={() => setAuditDetails(null)}
                titleAriaId="kusto-audit-details-title"
            >
                <Stack
                    tokens={{ childrenGap: 14 }}
                    styles={{
                        root: { padding: 24, width: 560, maxWidth: '90vw' },
                    }}
                >
                    <Stack
                        horizontal
                        horizontalAlign="space-between"
                        verticalAlign="center"
                    >
                        <Text
                            id="kusto-audit-details-title"
                            variant="xLarge"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            Audit record details
                        </Text>
                        <IconButton
                            iconProps={{ iconName: 'Cancel' }}
                            ariaLabel="Close"
                            onClick={() => setAuditDetails(null)}
                        />
                    </Stack>
                    <DetailField
                        label="Time (UTC)"
                        value={
                            auditDetails
                                ? new Date(
                                      auditDetails.timestampUtc
                                  ).toLocaleString()
                                : undefined
                        }
                    />
                    <DetailField label="Action" value={auditDetails?.action} />
                    <DetailField
                        label="Result"
                        value={auditDetails?.succeeded ? 'OK' : 'Failed'}
                    />
                    <DetailField
                        label="Application ID"
                        value={auditDetails?.applicationId}
                    />
                    <DetailField
                        label="Tenant ID"
                        value={auditDetails?.tenantId}
                    />
                    <DetailField
                        label="Display name"
                        value={auditDetails?.displayName}
                    />
                    <DetailField
                        label="Requested by"
                        value={auditDetails?.requestedBy}
                    />
                    <DetailField
                        label="Justification"
                        value={auditDetails?.justification}
                    />
                    <DetailField
                        label="Reviewer note"
                        value={auditDetails?.reviewNotes}
                    />
                    <DetailField
                        label="Message"
                        value={auditDetails?.message}
                    />
                </Stack>
            </Modal>
        </>
    );
};
